/**
 * Users service that abstracts user operations from Firebase to FastAPI.
 */

import apiClient, { ApiResponse } from './client';
import { AuthUser } from './auth';

export interface UserProfile extends AuthUser {
  timezone?: string;
  preferences?: Record<string, any>;
}

export interface UpdateUserProfileRequest {
  display_name?: string;
  timezone?: string;
  preferences?: Record<string, any>;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

export interface ProfilePictureUploadResponse {
  message: string;
  photo_url: string;
}


// Organization membership types
export interface UserOrganizationMembershipResponse {
  organizationId: string;
  role: 'admin' | 'member';
  joinedAt: string;
  organization?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
}

export interface UserOrganizationsResponse {
  organizations: UserOrganizationMembershipResponse[];
  total: number;
}

export interface UserRoleResponse {
  role: 'admin' | 'member' | null;
  isMember: boolean;
}

export interface MembershipStatusResponse {
  isMember: boolean;
  organizationId: string;
  userId: string;
}

export class UsersService {
  /**
   * Get current user's profile
   */
  async getCurrentUserProfile(): Promise<ApiResponse<UserProfile>> {
    return apiClient.get<UserProfile>('/api/users/me');
  }

  /**
   * Update current user's profile
   */
  async updateCurrentUserProfile(data: UpdateUserProfileRequest): Promise<ApiResponse<UserProfile>> {
    return apiClient.put<UserProfile>('/api/users/me', data);
  }

  /**
   * Change password
   */
  async changePassword(data: PasswordChangeRequest): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post<{ message: string }>('/api/users/me/change-password', data);
  }

  /**
   * Upload profile picture
   */
  async uploadProfilePicture(file: File): Promise<ApiResponse<ProfilePictureUploadResponse>> {
    return apiClient.uploadFile<ProfilePictureUploadResponse>('/api/users/me/upload-profile-picture', file);
  }

  /**
   * Delete user account
   */
  async deleteAccount(): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>('/api/users/me');
  }


  /**
   * Get user by ID (admin only)
   */
  async getUserById(userId: string): Promise<ApiResponse<UserProfile>> {
    return apiClient.get<UserProfile>(`/api/users/${userId}`);
  }

  /**
   * Get user by email (admin only)
   */
  async getUserByEmail(email: string): Promise<ApiResponse<UserProfile>> {
    return apiClient.get<UserProfile>(`/api/users/email/${encodeURIComponent(email)}`);
  }

  /**
   * Helper: Update user timezone
   */
  async updateTimezone(timezone: string): Promise<ApiResponse<UserProfile>> {
    return this.updateCurrentUserProfile({ timezone });
  }

  /**
   * Helper: Update user display name
   */
  async updateDisplayName(display_name: string): Promise<ApiResponse<UserProfile>> {
    return this.updateCurrentUserProfile({ display_name });
  }

  /**
   * Helper: Update user preferences
   */
  async updatePreferences(preferences: Record<string, any>): Promise<ApiResponse<UserProfile>> {
    return this.updateCurrentUserProfile({ preferences });
  }

  // Organization membership methods

  /**
   * Get current user's organization memberships
   */
  async getUserOrganizations(limit: number = 50, offset: number = 0): Promise<ApiResponse<UserOrganizationsResponse>> {
    const params = { limit: limit.toString(), offset: offset.toString() };
    const response = await apiClient.get<UserOrganizationsResponse>('/api/users/me/organizations', params);
    
    // Convert joinedAt strings to Date objects
    if (response.success && response.data) {
      response.data.organizations = response.data.organizations.map(org => ({
        ...org,
        joinedAt: org.joinedAt
      }));
    }
    
    return response;
  }

  /**
   * Get current user's role in a specific organization
   */
  async getUserRoleInOrganization(organizationId: string): Promise<ApiResponse<UserRoleResponse>> {
    return apiClient.get<UserRoleResponse>(`/api/users/me/organizations/${organizationId}/role`);
  }

  /**
   * Check if current user is a member of the organization
   */
  async checkMembershipStatus(organizationId: string): Promise<ApiResponse<MembershipStatusResponse>> {
    return apiClient.post<MembershipStatusResponse>(`/api/users/me/organizations/${organizationId}/membership`, {});
  }
}

// Create and export the default users service instance
const usersService = new UsersService();

export default usersService;
